
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/services/apiUtils';

// Tipo de usuário
export interface User {
  id: string;
  name: string;
  email: string;
  userType: 'Admin' | 'Membro' | 'VIP';
  avatar?: string;
  username?: string;
}

// Interface para tokens de autenticação
interface AuthTokens {
  AccessToken: string;
  IdToken: string;
  RefreshToken: string;
  ExpiresIn: number;
  TokenType: string;
}

// Interface do contexto de autenticação
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  register: (registerData: RegisterData) => Promise<void>;
  logout: () => void;
  recoverPassword: (email: string) => Promise<void>;
  setAuthenticatedUser: (user: User) => void;
}

// Interface para dados de registro
export interface RegisterData {
  username: string;
  email: string;
  preferred_username: string;
  password: string;
  gender: 'male' | 'female' | 'other';
}

// Criação do contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Função para decodificar o token JWT
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar token JWT:', error);
    return null;
  }
}

// Simulação de banco de dados de usuários para o registro
const mockUsers: Record<string, User & { password: string }> = {
  'admin@example.com': {
    id: '1',
    name: 'Administrador',
    email: 'admin@example.com',
    password: 'admin123',
    userType: 'Admin',
  },
  'membro@example.com': {
    id: '2',
    name: 'Carlos Silva',
    email: 'membro@example.com',
    password: 'membro123',
    userType: 'Membro',
  },
  'vip@example.com': {
    id: '3',
    name: 'Ana Costa',
    email: 'vip@example.com',
    password: 'vip123',
    userType: 'VIP',
  },
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Verificar se o usuário está logado ao carregar a página
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Erro ao recuperar usuário do localStorage:', error);
      localStorage.removeItem('user'); // Remove dados inválidos
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login sending username instead of email
  const login = async (usernameOrEmail: string, password: string) => {
    setIsLoading(true);
    
    try {
      // Send username and password in the request
      const loginData = {
        username: usernameOrEmail,
        password
      };
      
      console.log('Login data being sent:', loginData);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro no login');
      }
      
      const data = await response.json();
      
      // Extrair tokens
      const tokens: AuthTokens = data.tokens;
      
      // Decodificar o IdToken para obter informações do usuário
      const decodedToken = decodeJWT(tokens.IdToken);
      
      if (!decodedToken) {
        throw new Error('Token inválido ou mal formatado');
      }
      
      console.log('Decoded token:', decodedToken);
      console.log('Login username used:', usernameOrEmail);
      
      // Criar objeto de usuário a partir do token decodificado
      // IMPORTANTE: Use o username do login, não do token
      const userInfo: User = {
        id: decodedToken.sub,
        name: decodedToken.preferred_username || decodedToken.cognito_username || decodedToken.email,
        email: decodedToken.email,
        userType: 'Membro', // Defina como padrão ou extraia de algum claim específico
        username: usernameOrEmail, // SEMPRE usar o username do login
      };
      
      console.log('User info created with login username:', userInfo);
      
      // Salvar tokens e informações do usuário
      localStorage.setItem('authTokens', JSON.stringify(tokens));
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
      
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro de login:', error);
      toast.error('Falha no login. Verifique suas credenciais.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Registro
  const register = async (registerData: RegisterData) => {
    setIsLoading(true);
    
    try {
      // Validações básicas
      if (!validateEmail(registerData.email)) {
        throw new Error('Email inválido');
      }
      
      if (!validatePassword(registerData.password)) {
        throw new Error('A senha deve ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas, números e caracteres especiais');
      }
      
      if (!['male', 'female', 'other'].includes(registerData.gender)) {
        throw new Error('Gênero inválido');
      }
      
      // Enviar dados para o endpoint de registro
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro no registro');
      }
      
      const data = await response.json();
      
      // Se o registro for bem-sucedido, podemos receber tokens diretamente ou ter que fazer login
      if (data.tokens) {
        // Caso 1: O endpoint retorna tokens - procedemos como no login
        const tokens: AuthTokens = data.tokens;
        
        // Decodificar o IdToken para obter informações do usuário
        const decodedToken = decodeJWT(tokens.IdToken);
        
        if (!decodedToken) {
          throw new Error('Token inválido ou mal formatado');
        }
        
        // Criar objeto de usuário a partir do token decodificado
        const userInfo: User = {
          id: decodedToken.sub,
          name: decodedToken.preferred_username || registerData.preferred_username,
          email: decodedToken.email,
          userType: 'Membro', // Usuário padrão para novos registros
          username: decodedToken.cognito_username || registerData.username,
        };
        
        // Salvar tokens e informações do usuário
        localStorage.setItem('authTokens', JSON.stringify(tokens));
        localStorage.setItem('user', JSON.stringify(userInfo));
        setUser(userInfo);
        
        toast.success('Registro realizado com sucesso!');
        navigate('/dashboard');
      } else {
        // Caso 2: O endpoint apenas indica sucesso, mas precisamos fazer login separadamente
        toast.success('Registro realizado com sucesso! Por favor, faça login.');
        navigate('/'); // Direcionar para a página de login
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Falha no registro: ${error.message}`);
      } else {
        toast.error('Falha no registro. Tente novamente.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authTokens');
    setUser(null);
    toast.success('Logout realizado com sucesso!');
    navigate('/');
  };

  // Recuperação de senha
  const recoverPassword = async (email: string) => {
    setIsLoading(true);
    
    try {
      // Simulação de recuperação de senha
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (!mockUsers[email]) {
        throw new Error('Email não encontrado');
      }
      
      toast.success('Instruções de recuperação de senha enviadas para seu email!');
    } catch (error) {
      toast.error('Email não encontrado');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Função para definir manualmente o usuário autenticado
  const setAuthenticatedUser = (userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  // Função auxiliar para validar email
  function validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  // Função auxiliar para validar senha
  function validatePassword(password: string): boolean {
    // Pelo menos 8 caracteres, 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return re.test(password);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        recoverPassword,
        setAuthenticatedUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
